import { Dialog, DialogContent, Grid, IconButton, Typography, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import i18next from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DocTemplateVersionActions from "./DocTemplateVersionActions";
import PrefillDocumentTemplateToGenerate from "./PrefillDocumentTamplateToGenerate/PrefillDocumentTemplateToGenerate";
import { DownloadIcon, FilePenIcon } from "../../../assets/icons";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import useWindowSize from "../../../custom-hooks/useWindowSize/useWindowSize";
import {
  DocumentTemplateDTO,
  DocumentTemplateVersionDTO,
  DocumentTemplateVersionFilter,
} from "../../../models/document-templates";
import { DocumentTemplateVersionSTO } from "../../../models/document-templates/document-template-version/document-template-version-sto";
import { DocumentTemplateWithValuesDTO } from "../../../models/document-templates/document-template-version/document-template-with-values";
import { DocumentFileDTO } from "../../../models/documents/document-files";
import { StyledTextButton } from "../../../reusable-components/AddEditButton/styles";
import { NotAuthorizedPage } from "../../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import Table from "../../../reusable-components/Table/Table";
import { DocumentTemplateVersionApiService } from "../../../services/document-templates/document-template-version-api.service";
import { fillTemplates } from "../../../services/document-templates/word-template-api.service";
import { downloadBlob, handleDownloadFile } from "../../../services/documents";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_DT_TEMPLATES } from "../../../utils/constants/filter-tables-constants";
import { convertBase64ToBlob } from "../../../utils/files-utils/convert-file";
import { getFileExtension } from "../../../utils/files-utils/get-file-extension";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import DialogTitle from "../../DialogTitle/DialogTitle";

export interface State {
  isOpen: boolean;
}

interface Props {
  documentTemplate: DocumentTemplateDTO;
  handleNotifyParent: () => void;
  children?: React.ReactNode;
}

const ViewTemplateHistory = ({ documentTemplate, handleNotifyParent, children }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tRP = "components.document_templates.template_history";

  const { forceUpdate } = useForceUpdate();
  const [state, setState] = useState<State>({
    isOpen: false,
  });

  const { height } = useWindowSize();

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.CompanyAccountsAccess.LegalEntityAdminAccess,
    AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
    AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
    AUTHORIZATIONS.SystemAccess.CompanyAccountsConfig,
  ]);

  const handleCloseDialog = () => {
    setState((state) => ({ ...state, isOpen: false }));
  };

  const handleDownloadDocsFile = async (template: DocumentFileDTO, placeholders?: any) => {
    if (placeholders && Object.keys(placeholders).length > 0) {
      const confirmed = window.confirm(
        `${i18next.t(`components.upload_document.download_document_files_confirmation_message`)}`
      );

      if (confirmed) {
        try {
          const propsFilter = {
            ids: [template.id],
            placeHolders: placeholders,
          };

          const response = await fillTemplates(propsFilter, undefined);

          if (!response.error && response.items && response.items.length > 0) {
            const documentItem: DocumentTemplateWithValuesDTO = response.items[0];

            // Convert base64 content to blob using existing utility
            const fileContentBlob = convertBase64ToBlob(documentItem.content, documentItem.type);

            if (fileContentBlob) {
              const fileExtension = getFileExtension(template.name);
              const templateNameWithoutExt = template.name.replace(/\.[^/.]+$/, "");
              const fileName = `${templateNameWithoutExt}_filled.${fileExtension}`;

              downloadBlob(fileContentBlob, fileName);
              toastifyResponse(response);
            } else {
              toast("Error converting base64 content to blob");
            }
          } else {
            toast(response.error || "Error filling template - no data received");
          }
        } catch (error) {
          toast(error as string);
          console.error("Error downloading document:", error);
        }
      }
    } else {
      await handleDownloadFile(template);
    }
  };

  const handleDeleteVersion = async (versionItem: DocumentTemplateVersionDTO) => {
    try {
      const deletedVersion = {
        ...versionItem,
        isDeleted: true,
      } as DocumentTemplateVersionSTO;

      const response = await new DocumentTemplateVersionApiService().saveItems([deletedVersion]);

      if (!response.error) {
        if (handleNotifyParent) {
          handleNotifyParent();
        }
      } else {
        toastifyResponse(response, "failed");
      }
    } catch (error) {
      console.error("Error deleting version:", error);
      toast(error as string);
    }
  };

  const columns: GridColDef<DocumentTemplateVersionDTO>[] = [
    {
      field: "version",
      renderHeader: () => <Typography>{t(`common_words.version`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: ({ row }) => {
        return row.version;
      },
    },
    {
      field: "versionComment",
      renderHeader: () => <Typography>{`${t(`common_words.version_comment`)}`}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.8,
    },
    {
      field: "versionDate",
      renderHeader: () => <Typography>{`${t(`common_words.version_date`)}`}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.4,
      renderCell: ({ row }) => (
        <Grid>
          <Typography title={row.versionComment}>
            {row.versionDate ? intlFormatDateShort(row.versionDate) : ""}
          </Typography>
        </Grid>
      ),
      align: "center",
    },
    {
      field: "template",
      renderHeader: () => <Typography>{t(`${tRP}.template`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.3,
      sortable: false,
      align: "center",
      renderCell: ({ row }) => {
        return (
          <div>
            {row.template ? (
              <IconButton
                sx={{ fontSize: "1.3 em", color: theme.palette.primary.main }}
                title={t(`common_words.download`)}
                onClick={() => handleDownloadDocsFile(row.template!)}
              >
                <DownloadIcon />
              </IconButton>
            ) : (
              <Typography>No file</Typography>
            )}
          </div>
        );
      },
    },
    {
      field: "placeholders",
      renderHeader: () => <Typography>{t(`${tRP}.generate_document_from_template`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.3,
      sortable: false,
      align: "center",
      renderCell: ({ row }) => {
        return row.template ? (
          <PrefillDocumentTemplateToGenerate
            placeholders={row.placeHolders}
            template={row.template}
            handleNotifyParent={handleNotifyParent}
            version={row.version}
            versionComment={row.versionComment}
            documentTemplateName={documentTemplate.name}
            versionDate={row.versionDate}
          >
            <FilePenIcon color={theme.palette.primary.main} />
          </PrefillDocumentTemplateToGenerate>
        ) : (
          <></>
        );
        // <div style={{ display: "flex", justifyContent: "center" }}>
        //   {row.placeHolders && Object.keys(row.placeHolders).length > 0 ? (
        //     <EditDocumentPlaceholders
        //       item={row}
        //       renderAsIcon={true}
        //       onDownloadWithPlaceholders={(placeholders) => {
        //         if (row.template) {
        //           handleDownloadDocsFile(row.template, placeholders);
        //         }
        //       }}
        //     />
        //   ) : (
        //     <Typography>No file</Typography>
        //   )}
        // </div>
      },
    },
    {
      field: "",
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.2,
      sortable: false,
      align: "center",
      renderCell: ({ row }) => {
        return (
          <DocTemplateVersionActions
            item={row}
            handleNotifyParent={() => {
              handleNotifyParent();
              forceUpdate();
            }}
          />
        );
      },
    },
  ];

  const defaultItem: Partial<DocumentTemplateVersionDTO> = {
    id: "",
    version: "",
    versionComment: "",
    versionDate: null,
    template: null,
  };

  if (!hasAccess) {
    return <NotAuthorizedPage />;
  }

  const templateId = documentTemplate.id ?? "";

  return (
    <>
      {children ? (
        <Grid
          onClick={(e) => {
            e.stopPropagation();
            setState((state) => ({ ...state, isOpen: true }));
          }}
          style={{ cursor: "pointer" }}
        >
          {children}
        </Grid>
      ) : (
        <StyledTextButton onClick={() => setState((state) => ({ ...state, isOpen: true }))}>
          {`View history`}
        </StyledTextButton>
      )}

      <Dialog
        open={state.isOpen}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              maxWidth: "85% !important",
            },
          },
        }}
      >
        {state.isOpen && (
          <>
            <DialogTitle
              title={`${t(`${tRP}.versions`)}`}
              handleClose={handleCloseDialog}
            />
            <DialogContent>
              <Table
                hideRemoveIcon
                translationRootPath={tRP}
                columns={columns}
                allowEdit={false}
                readOnly
                defaultPageSize={5}
                tableHeight={height - height / 4}
                serviceApi={new DocumentTemplateVersionApiService()}
                defaultItem={defaultItem}
                propsFilter={
                  {
                    documentTemplateId: templateId,
                  } as DocumentTemplateVersionFilter
                }
                filter={FT_DT_TEMPLATES}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default ViewTemplateHistory;
